import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuthStore, useTemplateStore, useRenderStore } from "@/store";
import { getPathLanguage } from "@/lib/i18n";
import { translateCurrentPathText as t } from "@/lib/translations";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import LocalizedLink from "@/components/LocalizedLink";
import LanguageSelector from "@/components/LanguageSelector";
import RenderThumbnail from "@/components/RenderThumbnail";
import {
  Play,
  Plus,
  Download,
  CreditCard,
  Clock,
  Layers,
  LogOut,
  User,
  Settings,
  Sparkles,
  ArrowRight,
  CheckCircle,
  XCircle,
  Loader2,
  Crown,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useLocalizedNavigation();
  const language = getPathLanguage(location.pathname);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <LocalizedLink to="/" className="flex items-center gap-2">
            <img 
              src="/assets/logo-creavideos.png" 
              alt="CreaVideos" 
              className="h-8 w-auto"
            />
          </LocalizedLink>

          <div className="flex items-center gap-4">
            <LanguageSelector />
            <LocalizedLink to="/templates">
              <Button variant="ghost" className="hidden md:flex">
                <Layers className="w-4 h-4 mr-2" />
                Templates
              </Button>
            </LocalizedLink>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 rounded-full hover:bg-slate-100" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-teal-600" />
                  </div>
                  <span className="hidden md:block font-medium text-sm">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <LocalizedLink to="/account" className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    Account
                  </LocalizedLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <LocalizedLink to="/subscription" className="cursor-pointer">
                    <Crown className="w-4 h-4 mr-2" />
                    Subscription
                  </LocalizedLink>
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin Panel
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default function DashboardPage() {
  const { user, checkAuth } = useAuthStore();
  const { templates, fetchTemplates } = useTemplateStore();
  const { renders, fetchRenders, deleteRender } = useRenderStore();
  const location = useLocation();
  const navigate = useLocalizedNavigation();
  const language = getPathLanguage(location.pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const [notification, setNotification] = useState(null);
  const [reviewSettings, setReviewSettings] = useState(null);
  const [reviewForm, setReviewForm] = useState({ company_name: "", page_text: "", qr_text: "" });
  const [reviewSaving, setReviewSaving] = useState(false);
  const [qrDownloading, setQrDownloading] = useState(false);
  const reviewRecordingUrl = reviewSettings?.recording_url
    ? language === "en" ? reviewSettings.recording_url : `/${language}${reviewSettings.recording_url}`
    : "";

  const fetchReviewSettings = useCallback(async () => {
    try {
      const response = await axios.get(`/review-page-settings?lang=${language}`);
      setReviewSettings(response.data);
      setReviewForm({
        company_name: response.data.company_name || "",
        page_text: response.data.page_text || "",
        qr_text: response.data.qr_text || "",
      });
    } catch (error) {
      setReviewSettings(null);
    }
  }, [language]);

  useEffect(() => {
    checkAuth();
    fetchTemplates(true, language);
    fetchRenders();
    fetchReviewSettings();
  }, [checkAuth, fetchTemplates, fetchRenders, fetchReviewSettings, language]);

  // Handle OAuth callback in a separate effect
  useEffect(() => {
    const success = searchParams.get("success");
    const error = searchParams.get("error");

    if (!success && !error) return;

    // Use requestAnimationFrame to avoid synchronous setState in effect
    requestAnimationFrame(() => {
      if (success === "instagram_connected") {
        setNotification({
          type: "success",
          message: "Instagram account connected successfully!",
        });
      } else if (error) {
        const errorMessages = {
          invalid_callback: "Invalid authentication callback.",
          invalid_state: "Invalid authentication state.",
          token_exchange_failed: "Failed to exchange authentication token.",
          connection_failed: "Failed to connect Instagram. Please try again.",
        };
        setNotification({
          type: "error",
          message: errorMessages[error] || "An error occurred.",
        });
      }
      // Clear the query params
      setSearchParams({});
    });
  }, [searchParams, setSearchParams]);

  const handleReviewFieldChange = (field, value) => {
    setReviewForm((current) => ({ ...current, [field]: value }));
  };

  const handleSaveReviewSettings = async () => {
    setReviewSaving(true);
    try {
      await axios.put("/review-page-settings", reviewForm);
      await fetchReviewSettings();
      setNotification({ type: "success", message: "Client review page settings saved." });
    } catch (error) {
      setNotification({ type: "error", message: "Could not save client review settings." });
    } finally {
      setReviewSaving(false);
    }
  };

  const handleReviewLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post("/review-page-settings/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await fetchReviewSettings();
      setNotification({ type: "success", message: "Review page logo uploaded." });
    } catch (error) {
      setNotification({ type: "error", message: "Could not upload review page logo." });
    } finally {
      event.target.value = "";
    }
  };

  const handleRemoveReviewLogo = async () => {
    try {
      await axios.delete("/review-page-settings/logo");
      await fetchReviewSettings();
      setNotification({ type: "success", message: "Review page logo removed." });
    } catch (error) {
      setNotification({ type: "error", message: "Could not remove review page logo." });
    }
  };

  const handleDownloadQrPage = async () => {
    setQrDownloading(true);
    try {
      const response = await axios.get(`/review-page-settings/qr-download?lang=${language}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "client-review-qr.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setNotification({ type: "error", message: "Could not download the QR code page." });
    } finally {
      setQrDownloading(false);
    }
  };

  // Auto-dismiss notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const exportProgress = user ? Math.round((user.exports_used / user.export_limit) * 100) : 0;

  const handleDeleteRender = async (event, renderId) => {
    event.stopPropagation();

    const confirmed = window.confirm("Delete this render? This action cannot be undone.");
    if (!confirmed) return;

    await deleteRender(renderId);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "processing":
      case "queued":
        return <Loader2 className="w-4 h-4 text-violet-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Notification Banner */}
          {notification && (
            <div
              className={`mb-6 p-4 rounded-lg border ${
                notification.type === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
            >
              <div className="flex items-center">
                {notification.type === "success" ? (
                  <CheckCircle className="w-5 h-5 mr-2" />
                ) : (
                  <XCircle className="w-5 h-5 mr-2" />
                )}
                <span>{notification.message}</span>
                <button
                  onClick={() => setNotification(null)}
                  className="ml-auto text-sm underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-1">Welcome back, {user?.name?.split(" ")[0]}</h1>
              <p className="text-slate-600">Create and manage your video projects</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <LocalizedLink to="/templates">
                <Button className="btn-primary rounded-full" data-testid="create-new-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Video
                </Button>
              </LocalizedLink>
              <LocalizedLink to="/account">
                <Button variant="outline" className="rounded-full">
                  <User className="w-4 h-4 mr-2" />
                  My Account
                </Button>
              </LocalizedLink>
              <LocalizedLink to="/subscription">
                <Button variant="outline" className="rounded-full">
                  <CreditCard className="w-4 h-4 mr-2" />
                  My Subscription
                </Button>
              </LocalizedLink>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Exports Used</span>
                <span className="text-sm font-medium text-violet-600">
                  {user?.exports_used || 0} / {user?.export_limit || 0}
                </span>
              </div>
              <Progress value={exportProgress} className="h-2" />
              {exportProgress >= 100 && (
                <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {t("You reached your monthly exports limit. Upgrade your plan or buy extra video exports.")}
                  <LocalizedLink to="/pricing" className="ml-1 underline font-medium">
                    {t("Go to pricing")}
                  </LocalizedLink>
                </div>
              )}
              {exportProgress >= 80 && (
                <LocalizedLink to="/pricing" className="mt-3 text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1">
                  {t("Upgrade for more exports")}
                  <ArrowRight className="w-3 h-3" />
                </LocalizedLink>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                  <Download className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{renders.filter(r => r.status === "completed").length}</div>
                  <div className="text-slate-600 text-sm">Videos Rendered</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold capitalize">
                    {t(
                      user?.subscription_plan
                        ? `${user.subscription_plan.charAt(0).toUpperCase()}${user.subscription_plan.slice(1)}`
                        : "Free"
                    )}
                  </div>
                  <div className="text-slate-600 text-sm">Current Plan</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Renders */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold">Recent Renders</h2>
              <span className="text-xs text-slate-500">(Renders are automatically deleted after 2 months)</span>
            </div>
              {renders.length > 0 && (
                <LocalizedLink to="/renders" className="text-violet-600 hover:text-violet-700 text-sm font-medium">
                  View all
                </LocalizedLink>
              )}
            </div>

            {renders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Download className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No renders yet</h3>
                <p className="text-slate-600 mb-4">Start by selecting a template and creating your first video</p>
                <LocalizedLink to="/templates">
                  <Button className="btn-primary rounded-full">
                    Browse Templates
                  </Button>
                </LocalizedLink>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renders
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .slice(0, 6)
                  .map((render) => (
                  <div
                    key={render.id}
                    className="flex gap-3 bg-white rounded-xl border border-slate-200 p-1.5 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/renders/${render.id}`)}
                    data-testid={`render-${render.id}`}
                  >
                    <RenderThumbnail render={render} />
                    <div className="min-w-0 flex-1 py-2 pr-2">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(render.status)}
                          <span className="font-medium capitalize">{render.status}</span>
                        </div>
                        <span className="text-sm text-slate-500">
                          {new Date(render.created_at).toLocaleDateString()} {new Date(render.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                    {render.status === "processing" && (
                      <Progress value={render.progress || 0} className="h-1.5 mb-2" />
                    )}
                    {render.status === "completed" && render.output_url && (
                      <div className="flex items-center gap-2 mt-2" onClick={(event) => event.stopPropagation()}>
                        <Button size="sm" variant="outline" className="flex-1" asChild>
                          <a href={render.output_url} download>
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </a>
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={(event) => handleDeleteRender(event, render.id)}
                          aria-label="Delete render"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>



          <div className="mb-8 bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold mb-2">{t("Client review recording page settings")}</h2>
                <p className="text-sm text-slate-600">{t("Customize the public page your clients use to record video reviews, then share the link or download the printable QR page.")}</p>
              </div>
              {reviewSettings && (
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" size="sm" asChild>
                    <a href={reviewRecordingUrl} target="_blank" rel="noreferrer">{t("Open recording page")}</a>
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadQrPage} disabled={qrDownloading}>
                    {qrDownloading ? "Downloading..." : "Download QR code page"}
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="review-logo">{t("Upload your logo (optional)")}</Label>
                <Input id="review-logo" type="file" accept="image/*" onChange={handleReviewLogoUpload} />
                {reviewSettings?.logo_url && (
                  <div className="flex items-center gap-3">
                    <img src={reviewSettings.logo_url} alt="Review page logo" className="h-16 object-contain rounded border border-slate-200 p-2" />
                    <Button type="button" variant="outline" size="sm" onClick={handleRemoveReviewLogo}>
                      Remove logo
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="review-company-name">{t("Name of the establishment/company/place")}</Label>
                <Input id="review-company-name" value={reviewForm.company_name} onChange={(event) => handleReviewFieldChange("company_name", event.target.value)} />
              </div>

              <div className="space-y-2 lg:col-span-2">
                <Label htmlFor="review-page-text">{t("Text for the review recording page")}</Label>
                <Textarea id="review-page-text" rows={4} value={reviewForm.page_text} onChange={(event) => handleReviewFieldChange("page_text", event.target.value)} />
              </div>

              <div className="space-y-2 lg:col-span-2">
                <Label htmlFor="review-qr-text">{t("Text above the QR code")}</Label>
                <Textarea id="review-qr-text" rows={4} value={reviewForm.qr_text} onChange={(event) => handleReviewFieldChange("qr_text", event.target.value)} />
              </div>
            </div>

            <div className="mt-4">
              <Button className="btn-primary rounded-full" onClick={handleSaveReviewSettings} disabled={reviewSaving}>
                {reviewSaving ? t("Saving...") : t("Save client review settings")}
              </Button>
            </div>
          </div>

          {/* Templates */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Popular Templates</h2>
              <LocalizedLink to="/templates" className="text-violet-600 hover:text-violet-700 text-sm font-medium">
                View all
              </LocalizedLink>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {templates.slice(0, 4).map((template) => (
                <div
                  key={template.id}
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/editor/${template.id}`)}
                  data-testid={`template-${template.id}`}
                >
                  <div className="aspect-[4/5] bg-gradient-to-br from-violet-100 to-sky-100 relative">
                    {template.thumbnail_url ? (
                      <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-10 h-10 text-violet-400" />
                      </div>
                    )}
                    {template.layers?.some((l) => l.pixverse_prompt) && (
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-1 rounded-full bg-gradient-to-r from-teal-600 to-cyan-500 text-white text-xs font-medium flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          AI
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-sm group-hover:text-teal-600 transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      {template.canvas_width}×{template.canvas_height} • {template.duration}s
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
